// Where a visitor first arrived, for lead attribution.
//
// An enquiry's source_path is the page the form sits on, which is almost
// always /contact/. To tell which post or AI answer produced a lead, the
// browser remembers the first page it loaded and what sent the visitor there,
// and the contact form sends both.
//
// It lives in localStorage, not a cookie: a cookie would start a session and
// take the visitor out of Cloudflare's cache (EdgeCacheable), and Rails never
// sees a cached page view anyway. It holds a path and a source name, nothing
// about the person, and lapses after 30 days so the first touch stays recent.

const KEY = "osher.first_touch"
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000

export interface FirstTouch {
  landing_path: string
  landing_source: string | null
}

interface Stored extends FirstTouch {
  at: number
}

function read(): Stored | null {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return null
    const stored = JSON.parse(raw) as Stored
    return Date.now() - stored.at < MAX_AGE_MS ? stored : null
  } catch {
    return null
  }
}

// The campaign tag when the link carries one (ChatGPT tags its links
// utm_source=chatgpt.com), Google Ads' click id, else the referring site.
// Our own host as referrer, or none at all, is a direct visit.
function sourceOf(url: URL, referrer: string): string | null {
  const utm = url.searchParams.get("utm_source")
  if (utm) return utm
  if (url.searchParams.has("gclid")) return "google-ads"
  if (!referrer) return null
  try {
    const host = new URL(referrer).hostname
    return host === url.hostname ? null : host
  } catch {
    return null
  }
}

// Call once per full page load. Inertia visits do not reload the page, so the
// first load of a visit is the landing page.
export function rememberFirstTouch() {
  try {
    if (read()) return
    const url = new URL(window.location.href)
    const touch: Stored = {
      landing_path: url.pathname,
      landing_source: sourceOf(url, document.referrer),
      at: Date.now(),
    }
    window.localStorage.setItem(KEY, JSON.stringify(touch))
  } catch {
    // Storage can be disabled or full; attribution is a nice-to-have.
  }
}

export function firstTouch(): FirstTouch | null {
  const stored = read()
  return stored ? { landing_path: stored.landing_path, landing_source: stored.landing_source } : null
}
